import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIFormControlLabel from '@mui/material/FormControlLabel';
import MUICheckbox from '@mui/material/Checkbox';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import MUITooltip from "@mui/material/Tooltip";

import MUIStarIcon from '@mui/icons-material/Star';
import MUIWorkIcon from '@mui/icons-material/Work';
import MUILocationOnIcon from '@mui/icons-material/LocationOn';
import MUICalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import Card from "../../Card";
import DialogAtpDateTimePicker from "../../Dialog/DialogAtpDateTimePicker";
import ChipLocation from "../../Chip/ChipLocation";
import InfoItem from "../../InfoItem";
import Throttle from "../../../Utility/Throttle";
import ChipUser from "../../Chip/ChipUser";
import ChipAccount from "../../Chip/ChipAccount";
import ChipMarketingCampaign from "../../Chip/ChipMarketingCampaign";
import User from "../../../Utility/Crud/User";
import api from "../../../Seating/Security/api";
import Location from "../../../Utility/Crud/Location";
import MarketingCampaign from "../../../Utility/Crud/MarketingCampaign";
import Security from "../../../Utility/Security";

/**
 * CardChartSales renders a Card component
 * displaying patient sales information.
 * @returns {JSX.Element}
 */

const title = "Sales";
const icon = MUIWorkIcon;

const className = "CardChartSales";

export default function CardChartSales(props) {
    const context = useContext(MainContext);
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const orderActivity = chartContext.chart?.orderActivity[chartContext.orderId];
    const activity = chartContext.chart?.activity[chartContext.orderId];
    const originalOrder = chartContext.originalChart?.orderActivity[chartContext.orderId];
    const originalActivity = chartContext.originalChart?.activity[chartContext.orderId];
    const account = chartContext.chart?.account[activity?.accountId];
    const orderActivityApprovals = Object.values(chartContext.chart.orderActivityApprovals).find(orderActivityApproval => orderActivityApproval.orderActivityId === chartContext.orderId);
    const approval = Object.values(chartContext.chart?.approval || {}).find(approval => approval?.id === orderActivityApprovals?.approvalsId);
    const [editMode, setEditMode] = useState(false);
    const [cardChart, setCardChart] = useState(null);
    const [accountsFound, setAccountsFound] = useState([]);
    const [originalSelectedAccount, setOriginalSelectedAccount] = useState(account ? { label: account.name, value: account.id } : null);
    const [selectedAccount, setSelectedAccount] = useState(account ? { label: account.name, value: account.id } : null);
    const [isAtpDateTimePickerDialogOpen, setIsAtpDateTimePickerDialogOpen] = useState(false);

    useEffect(() => {
        if (account) {
            setAccountsFound([{ label: account.name, value: account.id }]);
        }
    }, []);

    useEffect(() => {
        if (editMode) {
            if (orderActivity.orderType === "Service" || orderActivity.orderType === "Pickup" || orderActivity.orderType === "Billing Issue") {
                chartContext.updateValidationRules({
                    key: className,
                    rules: [
                        {
                            target: "orderActivity",
                            id: chartContext.orderId,
                            field: "salesLocationId",
                            rule: {
                                "required": true
                            }
                        },
                        {
                            target: "orderActivity",
                            id: chartContext.orderId,
                            field: "serviceLocationId",
                            rule: {
                                "required": true
                            }
                        }
                    ]
                });
            } else {
                chartContext.updateValidationRules({
                    key: className,
                    rules: [
                        {
                            target: "orderActivity",
                            id: chartContext.orderId,
                            field: "salesLocationId",
                            rule: {
                                "required": true
                            }
                        },
                        {
                            target: "orderActivity",
                            id: chartContext.orderId,
                            field: "serviceLocationId",
                            rule: {
                                "required": true
                            }
                        },
                        {
                            target: "activity",
                            id: chartContext.orderId,
                            field: "accountId",
                            rule: {
                                "required": true
                            }
                        },
                    ]
                });
            }
        } else {
            chartContext.updateValidationRules({ key: className, rules: [] });
        }
    }, [editMode]);

    // Close edit mode if originalChart changes (on save)
    useEffect(() => {
        setEditMode(false);
    }, [chartContext.originalChart]);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    /**
     * Handles changes to the account search input.
     * Sends a POST request to fetch accounts matching the search term.
     * Updates the accountsFound state with the results.
     * @param {string} accountName - The name of the account being searched.
     * @param {string} accountId - The ID of the account being searched.
     */
    const handleAccountChange = (accountName, accountId) => {
        api.send("POST", `/search/omniSearch/account`, { omniSearchTerm: accountName, recordLimit: 200, account_ownerId: accountId }, 2, true)
            .then((data) => {
                let accounts = (data.accounts || []).map(account => ({
                    label: account.name,
                    value: account.id
                }));
                if (
                    selectedAccount &&
                    !accounts.some(a => a.value === selectedAccount.value)
                ) {
                    accounts.push(selectedAccount);
                }
                setAccountsFound(accounts.sort((a, b) => a.label.localeCompare(b.label)));
            })
            .catch((error) => {
                console.error("Error fetching accounts for ownerId:", error);
            });
    };

    /**
     * Throttled function to handle account search input changes.
     * Limits the frequency of API calls to once every 100 milliseconds.
     * @param {string} accountName - The name of the account being searched.
     * @param {string} accountId - The ID of the account being searched.
     */
    const throttledHandleAccountSearch = Throttle.throttle(
        (accountName, accountId) => {
            handleAccountChange(accountName, accountId);
        },
        100,
        { "trailing": true }
    );

    /**
     * Generates options for ATP (Account Team Person) selection dropdown.
     * Ensures that the currently selected ATP is included in the options
     * even if they are not active.
     * @param {string} locationIds - The location IDs to filter ATP options.
     * @returns {Array} Array of ATP options with label and value.
     */
    function getATPOptions(locationIds) {
        let users = User.getATPs(context.usersIndexed, context.userprofileLocationsIndexed, [locationIds]);
        const selectedUser = context.usersIndexed[orderActivity?.atpId];
        if (!users.includes(selectedUser) && selectedUser) {
            users.push({ ...selectedUser });
        }

        return users.map(user => ({
            label: User.getDisplayName(user) || "None",
            value: user.id
        })).sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Generates options for sales representative selection dropdown.
     * @param {*} locationIds
     * @returns
     */
    function getSalesRepOptions(locationIds) {
        let users = User.getSalesReps(context.usersIndexed, context.userprofileLocationsIndexed, [locationIds]);
        const selectedUser = context.usersIndexed[activity?.ownerId];
        if (!users.includes(selectedUser) && selectedUser) {
            users.push({ ...selectedUser });
        }
        return users.map(user => ({
            label: User.getDisplayName(user) || "None",
            value: user.id
        })).sort((a, b) => a.label.localeCompare(b.label));
    }

    /**
     * Generates options for location selection dropdown.
     * Ensures that the currently selected sales and service locations
     * are included in the options even if they are not active.
     * @returns {Array} Array of location options with label and value.
     */
    function getLocationOptions() {
        let locations;
        locations =
            Location.read(
                context.locationsIndexed,
                []
            );

        const selectedSalesLocation = context.locationsIndexed[orderActivity.salesLocationId];
        const selectedServiceLocation = context.locationsIndexed[orderActivity.serviceLocationId];
        if (selectedServiceLocation && !locations.includes(selectedServiceLocation)) {
            locations.push({ ...selectedServiceLocation });
        }
        if (selectedSalesLocation && !locations.includes(selectedSalesLocation)) {
            locations.push({ ...selectedSalesLocation });
        }

        locations = Location.sort(locations);
        return locations.map(location => ({
            label: location.name,
            value: location.id
        }));
    }

    /**
     *
     * @returns Array of marketing campaign options with label and value.
     * Ensures that the currently selected marketing campaign
     * is included in the options even if it is not active.
     */
    function getMarketingCampaignOptions() {
        let marketingCampaigns =
            MarketingCampaign.read(
                context.marketingCampaignsIndexed,
                [
                    {
                        key: "isActive",
                        value: true,
                    }
                ]
            );

        const selectedCampaign = context.marketingCampaignsIndexed[orderActivity?.marketingCampaignId];

        if (selectedCampaign && !marketingCampaigns.includes(selectedCampaign)) {
            marketingCampaigns.push({ ...selectedCampaign });
        }
        marketingCampaigns = MarketingCampaign.sort(marketingCampaigns);
        return marketingCampaigns.map(marketingCampaign => ({
            label: marketingCampaign.name,
            value: marketingCampaign.id
        }));
    }

    /**
     * Returns the styles for a container that should be full width at a given max width.
     * Only used when sidebar=true to handle responsive container queries.
     * @param {number} maxWidth - The max width at which the container should be full width.
     * @returns {Object} The styles for the container.
     */
    const getContainerQuery = (maxWidth) => {
        if (!props.sidebar) {
            return {}
        }

        return {
            [`@container (max-width: ${maxWidth}px)`]: { flexBasis: '100% !important', maxWidth: '100% !important' }
        }
    }

    /**
     * Renders the sales section of the patient sales.
     * @returns {JSX.Element} Grid layout with sales information display
     */
    const renderLocationSection = () => {
        return (
            <MUIGrid item xs={12}>
                <MUIGrid container spacing={2} >
                    <MUIGrid item xs={12}>
                        <MUIStack direction="row" spacing={1}>
                            <MUILocationOnIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Location</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIAutocomplete
                            options={getLocationOptions()}
                            value={getLocationOptions().find(option => option.value === orderActivity?.salesLocationId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            disableClearable
                            onChange={(e, newValue) => {
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { salesLocationId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, salesLocationId: newValue?.value || null })
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Sales Location"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].salesLocationId ? true : false}
                                    helperText={
                                        chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].salesLocationId
                                            ? chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].salesLocationId : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getLocationOptions()}
                            value={getLocationOptions().find(option => option.value === orderActivity?.serviceLocationId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            disableClearable
                            onChange={(e, newValue) => {
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { serviceLocationId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, serviceLocationId: newValue?.value || null })
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Service Location"
                                    fullWidth
                                    required={true}
                                    error={chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].serviceLocationId ? true : false}
                                    helperText={
                                        chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].serviceLocationId
                                            ? chartContext.validationIssues[className]?.orderActivity?.[chartContext.orderId].serviceLocationId : ""}
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIFormControlLabel
                            control={
                                <MUICheckbox
                                    size="small"
                                    checked={activity.priority || false}
                                    onChange={(e) => {
                                        chartContext.onChange({ activity: { [activity.id]: { priority: e.target.checked } } });
                                        setCardChart({ ...cardChart, priority: e.target.checked });
                                    }}
                                />
                            }
                            label="Priority"
                        />
                    </MUIGrid>
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     *
     * @returns {JSX.Element} Grid layout with marketing information display
     */
    const renderMarketingSection = () => {
        return (
            <MUIGrid item xs={12}>
                <MUIGrid container spacing={2}>
                    <MUIGrid item xs={12}>
                        <MUIStack direction="row" spacing={1}>
                            <MUIStarIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Marketing</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={getContainerQuery(600)}>
                        <MUIAutocomplete
                            options={getMarketingCampaignOptions()}
                            value={getMarketingCampaignOptions().find(option => option.value === orderActivity?.marketingCampaignId) || null}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { marketingCampaignId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, marketingCampaignId: newValue?.value || null })
                            }}
                            disableClearable={!Security.hasPermission("order.removeMarketingCampaign")}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Marketing Campaign"
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Renders the scheduled dates section of the patient sales.
     * @returns {JSX.Element} Grid layout with scheduled dates information display
     */
    const renderScheduledDatesSection = () => {
        return (
            <MUIGrid item xs={12}>
                <MUIGrid container spacing={2}>
                    <MUIGrid item xs={12}>
                        <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }} >
                            <MUICalendarMonthIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Scheduled Dates</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={"auto"} xs={12} sx={getContainerQuery(600)}>
                        <MUITooltip title={originalOrder?.isApproved === true ? "This order is already approved." : approval === null ? "No approval exists on this order" : ""} placement="bottom-end">
                            <span>
                                <MUIDatePicker
                                    label="Evaluation Date"
                                    defaultValue={orderActivity?.evaluationDate ? moment(orderActivity.evaluationDate) : null}
                                    disabled={originalOrder?.isApproved === true || approval === null}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: 'standard',
                                            InputLabelProps: {
                                                shrink: true
                                            },
                                            error: false,
                                        },
                                    }}
                                    onChange={(value) => {
                                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { evaluationDate: value ? moment(value).format('M/D/YYYY') : null } } })
                                        setCardChart({ ...cardChart, evaluationDate: value ? moment(value).format('M/D/YYYY') : null })
                                    }}
                                    views={['year', 'month', 'day']}
                                />
                            </span>
                        </MUITooltip>
                    </MUIGrid>
                    <MUIGrid item lg={"auto"} xs={12} sx={getContainerQuery(600)}>
                        <MUIDatePicker
                            label="F2F Date"
                            defaultValue={orderActivity?.faceToFaceDate ? moment(orderActivity.faceToFaceDate) : null}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    variant: 'standard',
                                    InputLabelProps: {
                                        shrink: true
                                    },
                                    error: false,
                                },
                            }}
                            onChange={(value) => {
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { faceToFaceDate: value ? moment(value).format('M/D/YYYY') : null } } })
                                setCardChart({ ...cardChart, faceToFaceDate: value ? moment(value).format('M/D/YYYY') : null })
                            }}
                            views={['year', 'month', 'day']}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={"auto"} xs={12} sx={getContainerQuery(600)}>
                        <div
                            onClick={() => setIsAtpDateTimePickerDialogOpen(true)}
                            style={{ cursor: "pointer" }}
                        >
                            <MUIDatePicker
                                label="ATP Evaluation Date"
                                value={orderActivity?.atpEvaluationDate ? moment(orderActivity.atpEvaluationDate) : null}
                                open={false}
                                sx={{ pointerEvents: "none" }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        variant: 'standard',
                                        InputLabelProps: {
                                            shrink: true
                                        },
                                        error: false,
                                    },
                                }}
                                views={['year', 'month', 'day']}
                            />
                        </div>
                    </MUIGrid>
                </MUIGrid>
                {isAtpDateTimePickerDialogOpen ? (
                    <DialogAtpDateTimePicker
                        open={isAtpDateTimePickerDialogOpen}
                        onClose={() => setIsAtpDateTimePickerDialogOpen(false)}
                        atpId={orderActivity?.atpId || null}
                        timestamp={orderActivity?.atpEvaluationDate || null}
                        salesLocationId={orderActivity?.salesLocationId || null}
                        loading={false}
                        onConfirm={(confirmedAtpEvaluationData) => {
                            const confirmedData = confirmedAtpEvaluationData || { timestamp: null, atpId: null };
                            chartContext.onChange({
                                orderActivity: {
                                    [chartContext.orderId]: {
                                        atpEvaluationDate: confirmedData.timestamp || null,
                                        atpId: confirmedData.atpId || null,
                                    }
                                }
                            });
                            setCardChart({
                                ...cardChart,
                                atpEvaluationDate: confirmedData.timestamp || null,
                                atpId: confirmedData.atpId || null,
                            });
                            setIsAtpDateTimePickerDialogOpen(false);
                        }}
                    />
                ) : null}
            </MUIGrid>
        )
    }

    /**
     *
     * @returns {JSX.Element} Grid layout with sales information display
     */
    const renderSalesSection = () => {
        const salesRepOptions = getSalesRepOptions(orderActivity?.salesLocationId);
        const salesRepValue = salesRepOptions.find(option => option.value === activity?.ownerId) || null;
        const atpOptions = getATPOptions(orderActivity?.salesLocationId);
        const atpValue = atpOptions.find(option => option.value === orderActivity?.atpId) || null;

        const atpAssessment = Object.values(chartContext.chart.atpAssessment).find((atpAssessment) => {
            return atpAssessment.orderActivityId === chartContext.orderId;
        }) || [];

        const documentSignature = Object.values(chartContext.chart.documentSignature).find((documentSignature) => {
            return documentSignature.orderActivityId === chartContext.orderId && atpAssessment.id === documentSignature.rowId;
        }) || null;

        return (
            <MUIGrid item xs={12}>
                <MUIGrid container spacing={2}>
                    <MUIGrid item xs={12}>
                        <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center' }} >
                            <MUIWorkIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                            <MUITypography variant="subtitle2">Sales</MUITypography>
                        </MUIStack>
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIAutocomplete
                            options={salesRepOptions}
                            value={salesRepValue}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ activity: { [chartContext.orderId]: { ...activity, ownerId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, ownerId: newValue?.value || null })
                            }}
                            disableClearable
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="Sales Rep"
                                    fullWidth
                                    required={true}
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIAutocomplete
                            options={atpOptions}
                            value={atpValue}
                            disabled={documentSignature ? !Security.hasRole(mainContext.currentUser, ["ADMIN"]) : false}
                            isOptionEqualToValue={(option, value) => option?.value === value?.value}
                            getOptionLabel={(option) => option?.label || ''}
                            onChange={(e, newValue) => {
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { atpId: newValue?.value || null } } })
                                setCardChart({ ...cardChart, atpId: newValue?.value || null })
                            }}
                            disableClearable={orderActivity?.atpEvaluationDate !== null}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    variant="standard"
                                    label="ATP"
                                    fullWidth
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIAutocomplete
                            options={accountsFound}
                            value={selectedAccount}
                            isOptionEqualToValue={(option, value) => {
                                const result = option?.value === value?.value;
                                return result;
                            }}
                            getOptionLabel={(option) => option?.label || ''}
                            disableClearable
                            onChange={(e, newValue) => {
                                setSelectedAccount(newValue);

                                chartContext.onChange({
                                    activity: { [chartContext.orderId]: { ...activity, accountId: newValue?.value || null } }
                                });
                                setCardChart({ ...cardChart, accountId: newValue?.value || null })
                            }}
                            onOpen={() => {
                                throttledHandleAccountSearch("", activity?.ownerId), [activity?.ownerId];
                            }}

                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(event) => {
                                        throttledHandleAccountSearch(event.target.value, activity?.ownerId);
                                    }}
                                    size="small"
                                    variant="standard"
                                    label="Account"
                                    fullWidth
                                    required={true}
                                    error={
                                        chartContext.validationIssues[className]?.activity?.[chartContext.orderId].accountId ? true : false
                                    }
                                    helperText={
                                        chartContext.validationIssues[className]?.activity?.[chartContext.orderId].accountId
                                            ? chartContext.validationIssues[className]?.activity?.[chartContext.orderId].accountId : ""
                                    }
                                />
                            )}
                        />
                    </MUIGrid>
                    <MUIGrid item lg={4} md={6} xs={12} sx={{ ...getContainerQuery(600) }}>
                        <MUIFormControlLabel
                            control={
                                <MUICheckbox
                                    size="small"
                                    checked={orderActivity.accountWasTarget || false}
                                    onChange={(e) => {
                                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { accountWasTarget: e.target.checked } } });
                                        setCardChart({ ...cardChart, accountWasTarget: e.target.checked });
                                    }}
                                />
                            }
                            label="Target Account"
                        />
                    </MUIGrid>
                </MUIGrid>
            </MUIGrid>
        )
    }

    /**
     * Renders the edit view with form fields for editing patient sales.
     * @returns {JSX.Element} Grid layout with editable form fields
     */
    const renderEditView = () => {
        return (
            <MUIGrid container spacing={4} rowSpacing={4} sx={{ containerType: 'inline-size' }}>
                {orderActivity.orderType === "Service" || orderActivity.orderType === "Pickup" || orderActivity.orderType === "Billing Issue" ? renderLocationSection() :
                    <>
                        {renderLocationSection()}
                        {renderSalesSection()}
                        {renderMarketingSection()}
                        {renderScheduledDatesSection()}
                    </>}
            </MUIGrid>
        )
    }

    /**
     * Renders the read-only view displaying patient sales information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderReadOnlyView = () => {
        const renderLocationOnly = (orderActivity?.orderType === "Service" || orderActivity?.orderType === "Pickup" || orderActivity?.orderType === "Billing Issue");
        return (
            <MUIGrid container spacing={3} rowSpacing={2}>
                <MUIGrid item xs={"auto"}>
                    <MUIStack sx={{ alignItems: 'flex-start' }}>
                        <MUITypography variant="subtitle2">Sales Location</MUITypography>
                        <ChipLocation location={context.locationsIndexed[orderActivity?.salesLocationId]} nullLabel={"None"} />
                    </MUIStack>
                </MUIGrid>
                <MUIGrid item xs={"auto"} >
                    <MUIStack sx={{ alignItems: 'flex-start' }}>
                        <MUITypography variant="subtitle2">Service Location</MUITypography>
                        <ChipLocation location={context.locationsIndexed[orderActivity?.serviceLocationId]} nullLabel={"None"} />
                    </MUIStack>
                </MUIGrid>
                {!renderLocationOnly && (
                    <>
                        {/* Sales Section */}
                        <MUIGrid item xs={"auto"}>
                            <MUIStack sx={{ alignItems: 'flex-start' }}>
                                <MUITypography variant="subtitle2">Sales Rep</MUITypography>
                                <ChipUser user={context.usersIndexed[activity?.ownerId]} nullLabel={"None"} />
                            </MUIStack>
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <MUIStack sx={{ alignItems: 'flex-start' }}>
                                <MUITypography variant="subtitle2">ATP</MUITypography>
                                <ChipUser user={context.usersIndexed[orderActivity?.atpId]} nullLabel={"None"} />
                            </MUIStack>
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <MUIStack sx={{ alignItems: 'flex-start' }}>
                                <MUITypography variant="subtitle2">Account</MUITypography>
                                <ChipAccount account={account} nullLabel={"None"} />
                            </MUIStack>
                        </MUIGrid>
                        <MUIGrid item xs={"auto"}>
                            <MUIStack sx={{ alignItems: 'flex-start' }}>
                                <MUITypography variant="subtitle2">Marketing Campaign</MUITypography>
                                <ChipMarketingCampaign marketingCampaign={context.marketingCampaignsIndexed[orderActivity?.marketingCampaignId]} nullLabel={"None"} />
                            </MUIStack>
                        </MUIGrid>
                        {/* Scheduled Dates Section */}
                        <MUIGrid item xs={12}>
                            <MUIStack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center', my: 2 }} >
                                <MUICalendarMonthIcon fontSize="medium" sx={{ color: (theme) => theme.palette.secondary.main }} />
                                <MUITypography variant="subtitle2">Scheduled Dates</MUITypography>
                            </MUIStack>
                            <MUIGrid container spacing={3} rowSpacing={1}>
                                <MUIGrid item xs={"auto"}>
                                    <InfoItem label="Evaluation Date" value={orderActivity?.evaluationDate ? moment(orderActivity.evaluationDate).format('M/D/YYYY') : "None"} />
                                </MUIGrid>
                                <MUIGrid item xs={"auto"}>
                                    <InfoItem label="F2F Date" value={orderActivity?.faceToFaceDate ? moment(orderActivity.faceToFaceDate).format('M/D/YYYY') : "None"} />
                                </MUIGrid>
                                <MUIGrid item xs={"auto"}>
                                    <InfoItem label="ATP Evaluation Date" value={orderActivity?.atpEvaluationDate ? moment(orderActivity.atpEvaluationDate).format('M/D/YYYY') : "None"} />
                                </MUIGrid>
                            </MUIGrid>
                        </MUIGrid>
                    </>
                )}
            </MUIGrid>
       

        )
    }

    /**
     * Determines which view to render based on edit mode state.
     * @returns {JSX.Element} Either edit view or read-only view
     */
    const renderContent = () => {
        if (editMode) {
            return (
                renderEditView()
            );
        }
        return (
            renderReadOnlyView()
        );
    }

    /**
     * Returns the action buttons for the card component.
     * @returns {Array} Array of action button components
     */
    const getActions = () => {
        const handleCancel = () => {
            const updatedActivity = { ...activity };
            const updatedOrderActivity = { ...orderActivity };

            const salesFields = cardChart ? Object.keys(cardChart) : [];

            for (const field of salesFields) {
                if (orderActivity[field] !== originalOrder[field]) {
                    updatedOrderActivity[field] = originalOrder[field];
                }
                if (activity[field] !== originalActivity[field]) {
                    updatedActivity[field] = originalActivity[field];
                }
            }

            chartContext.onChange({
                orderActivity: { [chartContext.orderId]: { ...updatedOrderActivity } },
                activity: { [chartContext.orderId]: { ...updatedActivity } }
            });

            setSelectedAccount(originalSelectedAccount);
            chartContext.setCardEditMode?.(className, false);
            setEditMode(false);

            const clearedIssues = { ...chartContext.validationIssues };
            delete clearedIssues[className];
            chartContext.updateValidationIssues(clearedIssues);
        }

        return [
            ...(editMode ? [
                <MUIButton
                    color="error"
                    disabled={chartContext.isUpdating}
                    onClick={handleCancel}
                    size="small"
                >
                    Cancel
                </MUIButton>
            ] : [
                <MUIButton
                    disabled={chartContext.isUpdating}
                    onClick={() => setEditMode(true)}
                    size="small"
                >
                    Edit
                </MUIButton>
            ])
        ];
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getContainerType={() => "inline-block"}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartSales.meta = { className: className, title: title, icon: icon };


